import {useParams} from "react-router-dom";
import React, {Dispatch, SetStateAction, useEffect, useRef, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {usernameSelector} from "src/control/selectors/commons/user_selectors";
import {isEmpty, setIfMounted} from "src/utils/common_utils";
import {Stages} from "src/view/modelTraining/constants";
import {Heading} from "src/view/style/home_styles";
import {KatButton, KatDivider, KatSpinner, KatTableRow, KatBadge} from "@amzn/katal-react";
import {PaddedSpan, Table, TableBody, TableCell, TableHead, TableHeadRow} from "src/view/style/table_styles";
import {InfoIcon} from "src/view/style/icons";
import {PopUp} from "src/view/style/common_styles";
import {ErrorContainer, ErrorHeading} from "src/view/style/modelTraining/errors";
import JSONEditor from "src/view/commons/json_editor";
import {getPrettyJsonString} from "src/utils/json_utils";
import {loadModelTrainingNamespace} from "src/control/actions/modelTraining/namespace_actions";
import {
    experimentationAccountInfraSelector,
    gammaAccountInfraSelector,
    namespaceSelector,
    prodAccountInfraSelector
} from "src/control/selectors/modelTraining/model_training_selectors";

export enum TABLE_HEADER {
    EXPERIMENTATION = "Experimentation",
    GAMMA = "Gamma",
    PROD = "Prod",
}

interface AccountInfo {
    infraInfoVisible: boolean,
    failedStacksVisible: boolean
}

const ViewNamespace = () => {
    const params: any = useParams()
    const dispatch = useDispatch();
    let _isMounted = useRef(true);

    const getDefaultInfoMap = () => {
        return {
            infraInfoVisible: false,
            failedStacksVisible: false
        }
    }

    const NO_ACCOUNT_ONBOARDED = "No account onboarded"
    const NO_PACKAGES_REGISTERED = "No code packages registered"
    const [expInfoMap, setExpInfoMap] = useState<AccountInfo>(getDefaultInfoMap())
    const [gammaInfoMap, setGammaInfoMap] = useState<AccountInfo>(getDefaultInfoMap())
    const [prodInfoMap, setProdInfoMap] = useState<AccountInfo>(getDefaultInfoMap())

    let userId = useSelector(usernameSelector);
    let experimentationAccountInfraData = useSelector(experimentationAccountInfraSelector);
    let gammaAccountInfraData = useSelector(gammaAccountInfraSelector);
    let prodAccountInfraData = useSelector(prodAccountInfraSelector);
    let fetchedNamespace = useSelector(namespaceSelector);

    useEffect(() => {
        _isMounted.current = true;
    });

    useEffect(() => {
        return () => {
            _isMounted.current = false;
        }
    });

    useEffect(() => {
        if (_isMounted.current) {
            loadNamespace()
        }
    }, [userId, params.teamId, params.namespaceId]);


    const loadNamespace = () => {
        dispatch(loadModelTrainingNamespace(
            params.teamId,
            params.namespaceId,
            [
                Stages.EXPERIMENTATION, Stages.GAMMA, Stages.PROD
            ]
        ))
    }

    const getInfrastructureInfoPopUp = (
        infraData: any,
        infraFetched: boolean,
        infraSuccessState: boolean,
        infoMap: AccountInfo,
        setInfoMap: Dispatch<SetStateAction<AccountInfo>>
    ) => {
        const jsonInfra = getPrettyJsonString(JSON.stringify(infraData.infraExports))
        return (
            <>
                <PopUp visible={infoMap.infraInfoVisible}
                       onOpen={(event: any) => {
                           setIfMounted(_isMounted.current, setInfoMap, {
                               ...infoMap,
                               infraInfoVisible: true
                           })
                       }}
                       onClose={(event: any) => {
                           setIfMounted(_isMounted.current, setInfoMap, {
                               ...infoMap,
                               infraInfoVisible: false
                           })
                       }}
                >
                    <span slot={"title"}>Infrastructure</span>
                    {infraFetched ?
                        <>
                            {
                                infraSuccessState ?
                                    <>
                                        <JSONEditor
                                            fieldName={"InfraJSON"}
                                            value={jsonInfra != null ? jsonInfra : "null"}
                                            onChange={() => {
                                            }}
                                            readOnly={true}
                                        />

                                    </> :
                                    <ErrorContainer>
                                        <ErrorHeading>Error loading Infra resources</ErrorHeading>
                                    </ErrorContainer>
                            }
                        </>
                        : <KatSpinner/>
                    }
                    <div slot="footer" className="kat-group-horizontal">
                        <KatButton label="Done" size="base" variant="primary" onClick={(event: any) => {
                            setIfMounted(_isMounted.current, setInfoMap, {
                                ...infoMap,
                                infraInfoVisible: false
                            })
                        }}/>
                    </div>
                </PopUp>
            </>
        );
    }

    const getFailedStacks = (
        infraData: any,
        infraFetched: boolean,
        infraSuccessState: boolean,
        infoMap: AccountInfo,
        setInfoMap: Dispatch<SetStateAction<AccountInfo>>
    ) => {
        return (
            <>
                <PopUp visible={infoMap.failedStacksVisible}
                       onOpen={(event: any) => {
                           setIfMounted(_isMounted.current, setInfoMap, {
                               ...infoMap,
                               failedStacksVisible: true
                           })
                       }}
                       onClose={(event: any) => {
                           setIfMounted(_isMounted.current, setInfoMap, {
                               ...infoMap,
                               failedStacksVisible: false
                           })
                       }}
                >
                    <span slot={"title"}>Infrastructure</span>
                    {infraFetched ?
                        <>
                            {
                                infraSuccessState ?
                                    <>
                                        {
                                            (!isEmpty(infraData.failedStacks) ?
                                                    <ul>
                                                        {
                                                            infraData.failedStacks.map(
                                                                (failedStack: string) => {
                                                                    return (
                                                                        <li>
                                                                            {failedStack}
                                                                        </li>
                                                                    )
                                                                })
                                                        }
                                                    </ul> :
                                                    "null"
                                            )
                                        }
                                    </> :
                                    <ErrorContainer>
                                        <ErrorHeading>Error loading Infra resources</ErrorHeading>
                                    </ErrorContainer>
                            }
                        </>
                        : <KatSpinner/>
                    }
                    <div slot="footer" className="kat-group-horizontal">
                        <KatButton label="Done" size="base" variant="primary" onClick={(event: any) => {
                            setIfMounted(_isMounted.current, setInfoMap, {
                                ...infoMap,
                                failedStacksVisible: false
                            })
                        }}/>
                    </div>
                </PopUp>
            </>
        );
    }

    const getTableHead = () => {
        return (
            <TableHead>
                <TableHeadRow>
                    <TableCell>
                        AccountId
                    </TableCell>
                    <TableCell>
                        Failed stacks
                    </TableCell>
                    <TableCell>
                        ClusterName
                    </TableCell>
                    <TableCell>
                        ClusterId
                    </TableCell>
                    <TableCell>
                        Resources
                    </TableCell>
                </TableHeadRow>
            </TableHead>
        )
    }

    const getTable = (infraData: any, tableHeader: TABLE_HEADER, infoMap: AccountInfo, setInfoMap: Dispatch<SetStateAction<AccountInfo>>) => {
        return (
            <>
                <Heading>{tableHeader}</Heading>
                {
                    <Table>
                        {
                            <>
                                {
                                    !isEmpty(infraData.accountId) ?
                                        <>
                                            {getTableHead()}
                                            <TableBody>
                                                <KatTableRow>
                                                    <TableCell>
                                                        {infraData.accountId}
                                                    </TableCell>
                                                    <TableCell>
                                                        <PaddedSpan>
                                                            <InfoIcon onClick={(event: any) => {
                                                                setIfMounted(_isMounted.current, setInfoMap, {
                                                                    ...infoMap,
                                                                    failedStacksVisible: true
                                                                })
                                                            }}/>
                                                        </PaddedSpan>
                                                    </TableCell>
                                                    <TableCell>
                                                        {infraData.clusterName ? infraData.clusterName : "-"}
                                                    </TableCell>
                                                    <TableCell>
                                                        {infraData.clusterId ? infraData.clusterId : "-"}
                                                    </TableCell>
                                                    <TableCell>
                                                        <PaddedSpan>
                                                            <InfoIcon onClick={(event: any) => {
                                                                setIfMounted(_isMounted.current, setInfoMap, {
                                                                    ...infoMap,
                                                                    infraInfoVisible: true
                                                                })
                                                            }}/>
                                                        </PaddedSpan>
                                                    </TableCell>
                                                </KatTableRow>
                                            </TableBody>
                                        </>
                                        :
                                        <>
                                            {NO_ACCOUNT_ONBOARDED}
                                        </>
                                }
                            </>
                        }
                    </Table>
                }
            </>
        )
    }

    const getSyncStatusBadge = (syncEnabled: boolean) => {
        switch (syncEnabled) {
            case true:
                return (<KatBadge label="Enabled"
                                  type="success"/>)
            case false:
                return (<KatBadge label="Disabled"
                                  type="warning"/>)
            default:
                return (<KatBadge label="Error"
                                  type="warning"/>)
        }
    }

    const getRegisteredCodePackagesTable = () => {
        return (
            <>
                {
                    <Table>
                        {
                            <>
                                {
                                    (!isEmpty(fetchedNamespace.namespaceData.codePackages) && fetchedNamespace.namespaceData.codePackages.length != 0) ?
                                        <>
                                            <TableHead>
                                                <KatTableRow>
                                                    <TableCell>Package Name</TableCell>
                                                    <TableCell>Sync Status</TableCell>
                                                </KatTableRow>
                                            </TableHead>
                                            <TableBody>
                                                {
                                                    fetchedNamespace.namespaceData.codePackages.map((codePackage: any) => (
                                                            <KatTableRow>
                                                                <TableCell> {codePackage.packageName}</TableCell>
                                                                <TableCell>
                                                                    {getSyncStatusBadge(codePackage.syncEnabled)}
                                                                </TableCell>
                                                            </KatTableRow>
                                                        )
                                                    )
                                                }
                                            </TableBody>
                                        </>
                                        :
                                        <>
                                            {NO_PACKAGES_REGISTERED}
                                        </>
                                }
                            </>
                        }
                    </Table>
                }
            </>
        )
    }


    return (
        <>
            {getInfrastructureInfoPopUp(
                experimentationAccountInfraData.infraData,
                experimentationAccountInfraData.infraFetched,
                experimentationAccountInfraData.infraFetchSuccess,
                expInfoMap,
                setExpInfoMap
            )}
            {getInfrastructureInfoPopUp(
                gammaAccountInfraData.infraData,
                gammaAccountInfraData.infraFetched,
                gammaAccountInfraData.infraFetchSuccess,
                gammaInfoMap,
                setGammaInfoMap
            )}
            {getInfrastructureInfoPopUp(
                prodAccountInfraData.infraData,
                prodAccountInfraData.infraFetched,
                prodAccountInfraData.infraFetchSuccess,
                prodInfoMap,
                setProdInfoMap
            )}
            {getFailedStacks(
                experimentationAccountInfraData.infraData,
                experimentationAccountInfraData.infraFetched,
                experimentationAccountInfraData.infraFetchSuccess,
                expInfoMap,
                setExpInfoMap
            )}
            {getFailedStacks(
                gammaAccountInfraData.infraData,
                gammaAccountInfraData.infraFetched,
                gammaAccountInfraData.infraFetchSuccess,
                gammaInfoMap,
                setGammaInfoMap
            )}
            {getFailedStacks(
                prodAccountInfraData.infraData,
                prodAccountInfraData.infraFetched,
                prodAccountInfraData.infraFetchSuccess,
                prodInfoMap,
                setProdInfoMap
            )}
            {
                fetchedNamespace.namespaceFetched ?
                    (
                        <div className="ViewNamespace">
                            {
                                fetchedNamespace.showNamespaceSuccess ?
                                    <>
                                        {getTable(experimentationAccountInfraData.infraData, TABLE_HEADER.EXPERIMENTATION, expInfoMap, setExpInfoMap)}
                                        <KatDivider variant="eastern"/>
                                        {getTable(gammaAccountInfraData.infraData, TABLE_HEADER.GAMMA, gammaInfoMap, setGammaInfoMap)}
                                        <KatDivider variant="eastern"/>
                                        {getTable(prodAccountInfraData.infraData, TABLE_HEADER.PROD, prodInfoMap, setProdInfoMap)}
                                    </>
                                    :
                                    <ErrorContainer>
                                        <ErrorHeading>Error loading Account Infrastructure details</ErrorHeading>
                                    </ErrorContainer>
                            }
                            <div>
                                <KatDivider variant="zircon"/>
                            </div>
                            <div className="RegisteredCodePackages">
                                {
                                    <Heading>
                                        Registered Code Packages
                                    </Heading>
                                }
                                {
                                    fetchedNamespace.showNamespaceSuccess ?
                                        getRegisteredCodePackagesTable()
                                        :
                                        <ErrorContainer>
                                            <ErrorHeading>Error loading registered code packages</ErrorHeading>
                                        </ErrorContainer>
                                }
                            </div>
                        </div>
                    )
                    :
                    <KatSpinner/>
            }
        </>
    );
}

export default ViewNamespace;